import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Euro, FileText, TrendingUp, AlertCircle, Users, ArrowRight, Inbox, Clock, Star, Flame,
} from "lucide-react";
import { api, STATUS_META, normalizeStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.en_attente;
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.color}`}>{m.label}</span>;
}

function greeting() {
  const h = new Date().getHours();
  return h < 18 ? "Bonjour" : "Bonsoir";
}

const FINAL = ["recuperee", "annulee"];

export default function AdminOverview() {
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setA(data)).catch(() => {});
    api.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => {});
  }, []);

  const recent = quotes.slice(0, 6);
  const priority = quotes.filter((q) => q.priority && !FINAL.includes(normalizeStatus(q.status)));

  const maxDay = Math.max(1, ...(a?.by_day || []).map((d) => d.count));
  const dayLabel = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", "");

  const kpis = [
    { label: "Chiffre d'affaires", value: a ? `${a.revenue.toFixed(2)} €` : "—", icon: Euro, c: "text-emerald-600 bg-emerald-50", testid: "kpi-revenue" },
    { label: "Demandes totales", value: a?.total ?? "—", icon: FileText, c: "text-blue-600 bg-blue-50", testid: "kpi-total" },
    { label: "Taux de conversion", value: a ? `${a.conversion}%` : "—", icon: TrendingUp, c: "text-violet-600 bg-violet-50", testid: "kpi-conversion" },
    { label: "Non attribuées", value: a?.unassigned ?? "—", icon: AlertCircle, c: "text-amber-600 bg-amber-50", testid: "kpi-unassigned" },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-blue-600">{greeting()},</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">{user?.name?.split(" ")[0]} 👋</h1>
          <p className="mt-1 text-sm text-slate-500">Vue d'ensemble de l'activité du magasin.</p>
        </div>
        <Link to="/app/admin/demandes"><Button className="bg-blue-600 hover:bg-blue-700">Voir les demandes <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
      </div>

      {/* KPIs */}
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={k.testid}>
            <div className="flex items-center justify-between">
              <span className={`grid h-10 w-10 place-items-center rounded-lg ${k.c}`}><k.icon className="h-5 w-5" /></span>
            </div>
            <p className="mt-4 font-display text-2xl font-bold text-slate-900">{k.value}</p>
            <p className="mt-1 text-sm text-slate-500">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Priority / urgent */}
      {priority.length > 0 && (
        <div className="mt-8 overflow-hidden rounded-2xl border-2 border-amber-300 bg-amber-50/50" data-testid="priority-section">
          <div className="flex items-center justify-between border-b border-amber-200 bg-amber-100/60 px-6 py-4">
            <div className="flex items-center gap-2 text-amber-800">
              <Flame className="h-5 w-5" />
              <h2 className="font-display text-lg font-semibold">Demandes prioritaires à traiter</h2>
              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-bold text-white">{priority.length}</span>
            </div>
            <Link to="/app/admin/demandes" className="text-sm font-medium text-amber-700 hover:underline">Voir tout</Link>
          </div>
          <div className="divide-y divide-amber-100">
            {priority.slice(0, 5).map((q) => (
              <Link key={q.id} to={`/app/demande/${q.id}`} className="flex items-center justify-between px-6 py-3.5 hover:bg-amber-100/40" data-testid={`priority-${q.id}`}>
                <div className="flex min-w-0 items-center gap-2.5">
                  <Star className="h-4 w-4 shrink-0 fill-amber-500 text-amber-500" />
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">{q.user_name} · <span className="text-slate-500">{q.vehicle?.brand} {q.vehicle?.model}</span></p>
                    <p className="line-clamp-1 text-xs text-slate-500">{q.parts_description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {q.assigned_to_name && <span className="hidden rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-600 sm:inline">{q.assigned_to_name}</span>}
                  <StatusBadge status={q.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        {/* 7-day chart */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <h2 className="font-display text-lg font-semibold">Demandes — 7 derniers jours</h2>
          <div className="mt-6 flex h-44 items-end justify-between gap-2" data-testid="chart-7days">
            {(a?.by_day || []).map((d) => (
              <div key={d.date} className="flex flex-1 flex-col items-center gap-2">
                <span className="text-xs font-semibold text-slate-500">{d.count}</span>
                <div className="flex w-full items-end" style={{ height: "120px" }}>
                  <div className="w-full rounded-t-md bg-blue-500 transition-all duration-500"
                    style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? "6px" : "2px", opacity: d.count ? 1 : 0.25 }} />
                </div>
                <span className="text-[11px] capitalize text-slate-400">{dayLabel(d.date)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Status breakdown */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-lg font-semibold">Répartition par étape</h2>
          <div className="mt-4 space-y-2.5" data-testid="status-breakdown">
            {["en_attente", "devis_envoye", "devis_valide", "en_preparation", "prete", "recuperee", "annulee"].map((st) => {
              const count = a?.by_status?.[st] || 0;
              if (!count) return null;
              return (
                <div key={st} className="flex items-center justify-between">
                  <StatusBadge status={st} />
                  <span className="font-semibold text-slate-700">{count}</span>
                </div>
              );
            })}
            {a && Object.keys(a.by_status || {}).length === 0 && <p className="text-sm text-slate-400">Aucune demande.</p>}
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4 text-sm">
            <div className="flex items-center gap-2 text-slate-600"><Users className="h-4 w-4 text-slate-400" /> {a?.clients ?? 0} clients</div>
            <div className="flex items-center gap-2 text-slate-600"><Clock className="h-4 w-4 text-slate-400" /> {a?.pending ?? 0} en attente</div>
          </div>
        </div>
      </div>

      {/* Recent */}
      <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-6 py-4">
          <h2 className="font-display text-lg font-semibold">Demandes récentes</h2>
          <Link to="/app/admin/demandes" className="text-sm font-medium text-blue-600 hover:underline">Tout voir</Link>
        </div>
        {recent.length === 0 ? (
          <div className="py-12 text-center"><Inbox className="mx-auto h-9 w-9 text-slate-300" /><p className="mt-2 text-sm text-slate-500">Aucune demande.</p></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {recent.map((q) => (
              <Link key={q.id} to={`/app/demande/${q.id}`} className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50" data-testid={`recent-${q.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-800">{q.user_name} · <span className="text-slate-500">{q.vehicle?.brand} {q.vehicle?.model}</span></p>
                  <p className="line-clamp-1 text-xs text-slate-400">{q.parts_description}</p>
                </div>
                <div className="flex items-center gap-3">
                  {q.assigned_to_name && <span className="hidden rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 sm:inline">{q.assigned_to_name}</span>}
                  <StatusBadge status={q.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
